import { AppDefinition, StoredAppDefinition } from '../types/apps';

import { API_BASE_URL } from '../api/config';
import fetchService from './fetchService';
import { requestCacheService } from './requestCacheService';
import { createFileLogger } from '../utils/logger';

// Session-level result cache for /apps/installed — stable within a page session
const INSTALLED_APPS_CACHE_TTL_MS = 60_000; // 1 minute
let _installedAppsResultCache: { data: AppDefinition[]; fetchedAt: number } | null = null;

class AppRegistryService {
  private apps: Map<string, StoredAppDefinition> = new Map();
  private dynamicApps: Map<string, React.ComponentType<any>> = new Map();
  private catalogInitialized = false;

  async initialize() {
    if (this.catalogInitialized) return;
    const log = createFileLogger('APP_REGISTRY_SERVICE');
    try {
      log.debug('Initializing...');
      const rawData = await fetchService(`${API_BASE_URL}/apps`);

      log.debug('Raw response:', rawData);

      // Ensure we have an array
      const apps = Array.isArray(rawData) ? rawData : [rawData];

      apps.forEach((app) => {
        if (!app) return;

        // Support both formats:
        //   New (system_app_registry): flat { appId, name, isCore, isSystem, ... }
        //   Old (DB App entity):       nested { skeleton: { appId, name, ... } }
        const appId: string | undefined = app.appId || app?.skeleton?.appId;
        if (!appId) {
          log.warn('App missing appId field:', app);
          return;
        }

        const now = new Date().toISOString();
        const storedApp: StoredAppDefinition = app.skeleton
          ? { ...app, _id: app._id?.toString() || 'unknown', created_at: app.created_at || now, updated_at: app.updated_at || now }
          : {
              _id: app._id?.toString() || 'unknown',
              entityType: 'App',
              skeleton: app,
              created_at: now,
              updated_at: now,
            };

        this.apps.set(appId, storedApp);
      });

      this.catalogInitialized = true;
      log.debug(`Initialized with ${this.apps.size} apps`);
    } catch (error) {
      log.error('Failed to initialize:', error);
      throw error;
    }
  }

  async installApp(appDefinition: AppDefinition) {
    const log = createFileLogger('APP_REGISTRY_SERVICE');
    log.debug('Installing app:', appDefinition);
    const appEntity: Partial<StoredAppDefinition> = {
      entityType: 'App',
      skeleton: appDefinition,
    };

    const installedApp: StoredAppDefinition = await fetchService(`${API_BASE_URL}/apps`, {
      method: 'POST',
      body: JSON.stringify(appEntity),
    });

    this.apps.set(installedApp.skeleton.appId, installedApp);
    _installedAppsResultCache = null;
    requestCacheService.invalidate('installed-apps');
    return installedApp;
  }

  async uninstallApp(appId: string) {
    await fetchService(`${API_BASE_URL}/apps/uninstall`, {
      method: 'POST',
      body: JSON.stringify({ appId }),
    });

    this.apps.delete(appId);
    _installedAppsResultCache = null;
    requestCacheService.invalidate('installed-apps');
  }

  async getInstalledApps(): Promise<AppDefinition[]> {
    const log = createFileLogger('APP_REGISTRY_SERVICE');
    
    // ✅ RESULT CACHE: Return cached result if within TTL to avoid duplicate fetches
    // Multiple components mount in sequence during page load — without this each fires
    // an independent GET /apps/installed (3x per page load was observed).
    if (_installedAppsResultCache && Date.now() - _installedAppsResultCache.fetchedAt < INSTALLED_APPS_CACHE_TTL_MS) {
      log.debug(`[getInstalledApps] Cache hit — returning ${_installedAppsResultCache.data.length} apps (cached)`);
      return _installedAppsResultCache.data;
    }

    // ✅ REQUEST DEDUPLICATION: prevent concurrent duplicate in-flight requests
    const apps: AppDefinition[] = await requestCacheService.getOrFetch(
      'installed-apps',
      () => fetchService(`${API_BASE_URL}/apps/installed`)
    );
    
    log.debug(`[getInstalledApps] Loaded ${apps.length} installed apps for user`);
    
    // Update local map for getAppDefinition() lookups
    apps.forEach((app: any) => {
      if (app.appId) {
        // Convert AppDefinition to StoredAppDefinition format for local cache
        const storedApp: StoredAppDefinition = {
          _id: app._id || 'unknown',
          entityType: 'App',
          skeleton: app,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        this.apps.set(app.appId, storedApp);
      }
    });
    
    // Deduplicate by appId (should already be unique from backend)
    const uniqueApps = new Map<string, AppDefinition>();
    apps.forEach((app) => {
      if (app.appId) {
        uniqueApps.set(app.appId, app);
      }
    });
    
    const result = Array.from(uniqueApps.values());
    // Populate result cache so subsequent calls within TTL skip the network
    _installedAppsResultCache = { data: result, fetchedAt: Date.now() };
    return result;
  }
  
  async getAvailableApps(): Promise<AppDefinition[]> {
    const log = createFileLogger('APP_REGISTRY_SERVICE');
    
    // Returns system apps (from code registry) + dynamic apps (from registered_apps collection)
    const apps: AppDefinition[] = await fetchService(`${API_BASE_URL}/apps/available`);
    
    log.debug(`[getAvailableApps] Loaded ${apps.length} available apps from catalog`);
    
    return apps;
  }

  getAllAppsEvents(): Record<string, string> {
    const events = {};
    this.apps.forEach((app) => {
      if (app.skeleton?.events) {
        Object.assign(events, app.skeleton.events);
      }
    });
    return events;
  }


  /**
   * Get app definition by appId (searches cached installed apps)
   * NEW: For dynamic tool loading
   */
  getAppDefinition(appId: string): AppDefinition | undefined {
    // Check cached apps (populated by getInstalledApps)
    const installedApp = this.apps.get(appId);
    if (installedApp) {
      return installedApp.skeleton;
    }

    return undefined;
  }
  
  /**
   * Register a dynamic app component (Phase 2)
   */
  registerDynamicApp(appId: string, Component: React.ComponentType<any>): void {
    const log = createFileLogger('APP_REGISTRY_SERVICE');
    log.info('[registerDynamicApp] Registering:', appId);
    this.dynamicApps.set(appId, Component);
  }
  
  /**
   * Get dynamic app component (Phase 2)
   */
  getDynamicApp(appId: string): React.ComponentType<any> | undefined {
    return this.dynamicApps.get(appId);
  }
  
  /**
   * Check if app is dynamic (Phase 2)
   */
  isDynamicApp(appId: string): boolean {
    return this.dynamicApps.has(appId);
  }
  
  /**
   * Unregister dynamic app (Phase 2)
   */
  unregisterDynamicApp(appId: string): void {
    const log = createFileLogger('APP_REGISTRY_SERVICE');
    log.info('[unregisterDynamicApp] Unregistering:', appId);
    this.dynamicApps.delete(appId);
  }
  
  /**
   * Reload dynamic app (Phase 2)
   */
  async reloadDynamicApp(appId: string, userId: string): Promise<void> {
    const log = createFileLogger('APP_REGISTRY_SERVICE');
    log.info('[reloadDynamicApp] Reloading:', appId);
    
    // Load the new version before unregistering the old one — if load fails,
    // the existing app stays registered so users are not left with a broken state.
    const availableApps: any[] = await fetchService(`${API_BASE_URL}/apps/available?appId=${appId}`);
    const appDoc = Array.isArray(availableApps) ? availableApps.find((a: any) => a.appId === appId) : null;

    if (!appDoc) {
      throw new Error(`App ${appId} not found in registry`);
    }

    // Wrap in the entity shape that dynamicAppLoader.load() expects
    const appEntity = { _id: appDoc._id, entityType: 'App', skeleton: appDoc };

    const { dynamicAppLoader } = await import('./dynamicAppLoader');
    const result = await dynamicAppLoader.load(appEntity, userId);

    if (!result.success || !result.bundledCode) {
      throw new Error(result.error || 'Failed to reload app');
    }

    // Only unregister after confirming the new version loaded successfully
    this.unregisterDynamicApp(appId);
    log.info('[reloadDynamicApp] Reloaded successfully:', appId);
  }
}

export const appRegistry = new AppRegistryService();
